<template>
    <section class="prompt-assistant-card" :class="[
        `prompt-assistant-card--${agent}`,
        { 'prompt-assistant-card--history': variant === 'history' },
    ]" :data-agent-key="content.agentKey" :title="copy.guide" role="button" tabindex="0" @click="handleCardClick"
        @keydown.enter.self="handleCardKeydown" @keydown.space.self.prevent="handleCardKeydown">
        <span v-if="variant === 'history'" class="prompt-assistant-card__history-symbols" aria-hidden="true">
            <svg v-for="symbolClass in ['symbol-1', 'symbol-2', 'symbol-3']" :key="symbolClass"
                :class="['prompt-assistant-card__history-symbol', symbolClass]" viewBox="0 0 24 24"
                v-html="historyIconSvg" />
        </span>
        <button v-if="variant !== 'history'" type="button" class="prompt-assistant-card__close" :aria-label="copy.heading"
            @click.stop="$emit('dismiss')">
            <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 3l10 10M13 3L3 13" />
            </svg>
        </button>

        <img class="prompt-assistant-card__avatar" :src="content.avatarUrl" :alt="`${content.name}: ${copy.heading}`"
            referrerpolicy="strict-origin-when-cross-origin" />
        <div class="prompt-assistant-card__content">
            <template v-if="variant === 'history'">
                <div class="prompt-assistant-card__history-title">
                    <strong>{{ content.name }}</strong>
                    <span>{{ historyCopy.role }}</span>
                </div>
                <p>{{ historyCopy.description }}</p>
            </template>
            <template v-else>
                <strong>{{ copy.heading }}</strong>
                <p>{{ copy.guide }}</p>
            </template>
        </div>
        <button type="button" class="prompt-assistant-card__action" :aria-label="`${content.name}: ${copy.guide}`"
            @click.stop="openAgent">
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
            </svg>
        </button>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import {
    resolveExpertAgentMainAppLocale,
    resolveExpertAgentMainAppOrigin,
} from '~/utils/expertAgentRequest'

export type PromptAssistantAgent = 'zeny' | 'vlo' | 'lydi'
export type PromptAssistantCardVariant = 'prompt' | 'history'
type PromptAssistantCopy = { heading: string; guide: string }
type PromptAssistantHistoryCopy = { role: string; description: string }

defineEmits<{
    (e: 'dismiss'): void
}>()

const props = withDefaults(
    defineProps<{
        agent: PromptAssistantAgent
        variant?: PromptAssistantCardVariant
    }>(),
    { variant: 'prompt' }
)
const { locale } = useI18n()
const userStore = useUserStore()

/** 来自 Agent 列表接口的数据快照；展示字段不再维护独立的本地文案或头像。 */
const contentByAgent = {
    zeny: {
        id: 40,
        agentKey: 'graphic-designer-zeny',
        name: 'Zeny',
        title: '设计师',
        shortDescription: '视觉设计与创意呈现',
        description: '告诉 Zeny 你的创意，它会生成精美图片并优化视觉效果。',
        avatarUrl: 'https://materials.wery.ai/static/agents/zeny.gif',
    },
    vlo: {
        id: 47,
        agentKey: 'video-gen-vlo',
        name: 'Vlo',
        title: '视频生成',
        shortDescription: '将动态意图转化为可执行的视频',
        description:
            '告诉 Vlo 你的视觉想法，它会快速生成带镜头动态与特效的短视频片段。',
        avatarUrl: 'https://materials.wery.ai/static/agents/vlo.gif',
    },
    lydi: {
        id: 43,
        agentKey: 'music-gen-lydi',
        name: 'Lydi',
        title: '音乐生成',
        shortDescription: '为作品赋予声音的情绪与情感的节奏',
        description:
            '告诉 Lydi 您的心情、视觉画面或使用场景，它将为您量身打造完美的音乐和配乐。',
        avatarUrl: 'https://materials.wery.ai/static/agents/lydi.gif',
    },
} satisfies Record<PromptAssistantAgent, Record<string, string | number>>

const content = computed(() => contentByAgent[props.agent])

/** 参考项目的历史卡片装饰图标，作为原始 SVG 片段写入以避免条件节点的命名空间兼容问题。 */
const historyIconSvgByAgent: Record<PromptAssistantAgent, string> = {
    zeny: '<rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.5" /><path d="m6.5 17 4.2-4.3 3.1 3 1.8-2.1 2.9 3.4" />',
    vlo: '<rect x="4" y="7" width="11" height="10" rx="2" /><path d="m15 10 5-3v10l-5-3z" />',
    lydi: '<path d="M9 18V6l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" />',
}
const historyIconSvg = computed(() => historyIconSvgByAgent[props.agent])

const fallbackAssistantCopy: Record<PromptAssistantAgent, PromptAssistantCopy> = {
    zeny: {
        heading: 'Need help writing prompts?',
        guide: 'Let Zeny refine your creative idea and description',
    },
    vlo: {
        heading: 'Not sure how to make a video?',
        guide: 'Let Vlo complete your story and shot description',
    },
    lydi: {
        heading: 'No music inspiration?',
        guide: 'Let Lydi shape your melody and mood',
    },
}

const assistantCopyByLocale: Record<
    string,
    Record<PromptAssistantAgent, PromptAssistantCopy>
> = {
    zh: {
        zeny: { heading: '不会写提示词？', guide: '让 Zeny 帮你补全创意和描述' },
        vlo: { heading: '不会做视频？', guide: '让 Vlo 帮你补全故事和镜头描述' },
        lydi: { heading: '没有音乐灵感？', guide: '让 Lydi 帮你写出旋律和情绪' },
    },
    tw: {
        zeny: { heading: '不會寫提示詞？', guide: '讓 Zeny 幫你補全創意和描述' },
        vlo: { heading: '不會做影片？', guide: '讓 Vlo 幫你補全故事和鏡頭描述' },
        lydi: { heading: '沒有音樂靈感？', guide: '讓 Lydi 幫你寫出旋律和情緒' },
    },
    en: fallbackAssistantCopy,
    ja: {
        zeny: { heading: 'プロンプトの書き方に困っていますか？', guide: 'Zeny がアイデアと説明を補完します' },
        vlo: { heading: '動画の作り方に困っていますか？', guide: 'Vlo がストーリーとカット説明を補完します' },
        lydi: { heading: '音楽のアイデアがありませんか？', guide: 'Lydi がメロディーと雰囲気を整えます' },
    },
    ko: {
        zeny: { heading: '프롬프트 작성이 어려우신가요?', guide: 'Zeny가 아이디어와 설명을 보완해 드립니다' },
        vlo: { heading: '동영상 제작이 어려우신가요?', guide: 'Vlo가 스토리와 장면 설명을 보완해 드립니다' },
        lydi: { heading: '음악 영감이 없으신가요?', guide: 'Lydi가 멜로디와 분위기를 다듬어 드립니다' },
    },
    es: {
        zeny: { heading: '¿Necesitas ayuda con los prompts?', guide: 'Zeny puede perfeccionar tu idea y descripción' },
        vlo: { heading: '¿No sabes cómo crear un video?', guide: 'Vlo completa tu historia y descripción de planos' },
        lydi: { heading: '¿Sin inspiración musical?', guide: 'Lydi da forma a tu melodía y ambiente' },
    },
    fr: {
        zeny: { heading: 'Besoin d’aide pour vos prompts ?', guide: 'Zeny affine votre idée et votre description' },
        vlo: { heading: 'Vous ne savez pas créer une vidéo ?', guide: 'Vlo complète votre histoire et vos plans' },
        lydi: { heading: 'En manque d’inspiration musicale ?', guide: 'Lydi façonne votre mélodie et votre ambiance' },
    },
    de: {
        zeny: { heading: 'Hilfe beim Schreiben von Prompts?', guide: 'Zeny verfeinert Ihre Idee und Beschreibung' },
        vlo: { heading: 'Unsicher beim Erstellen eines Videos?', guide: 'Vlo ergänzt Ihre Story und Szenenbeschreibung' },
        lydi: { heading: 'Keine musikalische Inspiration?', guide: 'Lydi gestaltet Melodie und Stimmung' },
    },
    ru: {
        zeny: { heading: 'Нужна помощь с промптами?', guide: 'Zeny дополнит вашу идею и описание' },
        vlo: { heading: 'Не знаете, как создать видео?', guide: 'Vlo дополнит историю и описание сцен' },
        lydi: { heading: 'Нет музыкального вдохновения?', guide: 'Lydi поможет с мелодией и настроением' },
    },
    pl: {
        zeny: { heading: 'Potrzebujesz pomocy z promptami?', guide: 'Zeny dopracuje Twój pomysł i opis' },
        vlo: { heading: 'Nie wiesz, jak stworzyć wideo?', guide: 'Vlo uzupełni historię i opis ujęć' },
        lydi: { heading: 'Brakuje inspiracji muzycznej?', guide: 'Lydi pomoże stworzyć melodię i nastrój' },
    },
    pt: {
        zeny: { heading: 'Precisa de ajuda com prompts?', guide: 'Zeny aprimora sua ideia e descrição' },
        vlo: { heading: 'Não sabe como criar um vídeo?', guide: 'Vlo completa sua história e descrição de cenas' },
        lydi: { heading: 'Sem inspiração musical?', guide: 'Lydi cria a melodia e o clima para você' },
    },
    it: {
        zeny: { heading: 'Hai bisogno di aiuto con i prompt?', guide: 'Zeny perfeziona la tua idea e descrizione' },
        vlo: { heading: 'Non sai come creare un video?', guide: 'Vlo completa la storia e la descrizione delle scene' },
        lydi: { heading: 'Nessuna ispirazione musicale?', guide: 'Lydi dà forma a melodia e atmosfera' },
    },
    ar: {
        zeny: { heading: 'هل تحتاج إلى مساعدة في كتابة المطالبات؟', guide: 'تساعدك Zeny على صقل فكرتك ووصفك' },
        vlo: { heading: 'لست متأكدًا من كيفية إنشاء فيديو؟', guide: 'تساعدك Vlo على إكمال القصة ووصف اللقطات' },
        lydi: { heading: 'لا تملك إلهامًا موسيقيًا؟', guide: 'تساعدك Lydi على صياغة اللحن والأجواء' },
    },
    th: {
        zeny: { heading: 'ต้องการความช่วยเหลือในการเขียนพรอมต์ไหม?', guide: 'Zeny ช่วยเติมไอเดียและคำอธิบายของคุณ' },
        vlo: { heading: 'ไม่แน่ใจว่าจะสร้างวิดีโออย่างไร?', guide: 'Vlo ช่วยเติมเรื่องราวและคำอธิบายช็อต' },
        lydi: { heading: 'ไม่มีไอเดียด้านดนตรีใช่ไหม?', guide: 'Lydi ช่วยสร้างทำนองและอารมณ์' },
    },
    da: {
        zeny: { heading: 'Brug for hjælp til prompts?', guide: 'Zeny forfiner din idé og beskrivelse' },
        vlo: { heading: 'Usikker på, hvordan du laver en video?', guide: 'Vlo fuldender din historie og billedbeskrivelse' },
        lydi: { heading: 'Mangler du musikalsk inspiration?', guide: 'Lydi former din melodi og stemning' },
    },
    no: {
        zeny: { heading: 'Trenger du hjelp med prompten?', guide: 'Zeny forbedrer ideen og beskrivelsen din' },
        vlo: { heading: 'Usikker på hvordan du lager video?', guide: 'Vlo fullfører historien og scenebeskrivelsen din' },
        lydi: { heading: 'Mangler du musikalsk inspirasjon?', guide: 'Lydi former melodi og stemning' },
    },
    nl: {
        zeny: { heading: 'Hulp nodig bij prompts?', guide: 'Zeny verfijnt je idee en beschrijving' },
        vlo: { heading: 'Weet je niet hoe je een video maakt?', guide: 'Vlo vult je verhaal en shotbeschrijving aan' },
        lydi: { heading: 'Geen muzikale inspiratie?', guide: 'Lydi geeft vorm aan je melodie en sfeer' },
    },
    id: {
        zeny: { heading: 'Butuh bantuan menulis prompt?', guide: 'Zeny menyempurnakan ide dan deskripsi Anda' },
        vlo: { heading: 'Tidak yakin cara membuat video?', guide: 'Vlo melengkapi cerita dan deskripsi adegan Anda' },
        lydi: { heading: 'Tidak ada inspirasi musik?', guide: 'Lydi membantu membentuk melodi dan suasana' },
    },
    tr: {
        zeny: { heading: 'Prompt yazmak için yardıma mı ihtiyacınız var?', guide: 'Zeny fikrinizi ve açıklamanızı geliştirir' },
        vlo: { heading: 'Video oluşturmayı bilmiyor musunuz?', guide: 'Vlo hikâyenizi ve sahne açıklamanızı tamamlar' },
        lydi: { heading: 'Müzik ilhamınız mı yok?', guide: 'Lydi melodi ve atmosferinizi şekillendirir' },
    },
}

const copy = computed(
    () =>
        assistantCopyByLocale[locale.value]?.[props.agent] ??
        fallbackAssistantCopy[props.agent]
)

const fallbackHistoryCopy: Record<
    PromptAssistantAgent,
    PromptAssistantHistoryCopy
> = {
    zeny: {
        role: 'Visual design partner',
        description:
            'Turn abstract ideas into consistent, recognizable visual directions.',
    },
    vlo: {
        role: 'Video creation partner',
        description:
            'Complete shots, movement, and rhythm to bring your story to life.',
    },
    lydi: {
        role: 'Music creation partner',
        description:
            'Organize musical style, lyric expression, and atmosphere for easier creation.',
    },
}

const historyCopyByLocale: Record<
    string,
    Record<PromptAssistantAgent, PromptAssistantHistoryCopy>
> = {
    zh: {
        zeny: {
            role: '设计师',
            description: '把模糊想法变成统一、有辨识度的视觉方案。',
        },
        vlo: {
            role: '视频生成',
            description: '补全镜头、动作和节奏，让故事动起来。',
        },
        lydi: {
            role: '音乐创作伙伴',
            description: '帮你整理音乐风格、歌词表达和整体氛围，让创作更省心。',
        },
    },
    tw: {
        zeny: {
            role: '設計師',
            description: '把模糊想法變成統一且具辨識度的視覺方案。',
        },
        vlo: {
            role: '影片生成',
            description: '補全鏡頭、動作和節奏，讓故事動起來。',
        },
        lydi: {
            role: '音樂創作夥伴',
            description: '幫你整理音樂風格、歌詞表達和整體氛圍，讓創作更省心。',
        },
    },
    en: fallbackHistoryCopy,
}

const historyCopy = computed(
    () =>
        historyCopyByLocale[locale.value]?.[props.agent] ??
        fallbackHistoryCopy[props.agent]
)

function openAgent() {
    if (!import.meta.client) return

    const targetLocale = resolveExpertAgentMainAppLocale(locale.value)
    // 与顶部专家悬浮菜单一致：跳 .ai 首页，并用 Agent 展示名选中对应角色。
    const returnPath = `/${targetLocale}?agent=${encodeURIComponent(content.value.name)}`
    const mainAppUrl = new URL(returnPath, resolveExpertAgentMainAppOrigin())
    const hasGrowthSession = Boolean(userStore.token || localStorage.getItem('token'))

    if (!hasGrowthSession) {
        window.open(mainAppUrl.toString(), '_blank', 'noopener,noreferrer')
        return
    }

    const handoffUrl = new URL('/sso/handoff', window.location.origin)
    handoffUrl.searchParams.set('return_path', returnPath)
    window.open(handoffUrl.toString(), '_blank', 'noopener,noreferrer')
}

function handleCardClick() {
    openAgent()
}

function handleCardKeydown() {
    openAgent()
}
</script>

<style scoped lang="less">
.prompt-assistant-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 64px;
    margin-top: 10px;
    padding: 8px 46px 8px 10px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    background: #1c1c20;
    color: #fff;
    cursor: pointer;
}

.prompt-assistant-card__avatar {
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    object-fit: contain;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
}

.prompt-assistant-card__content {
    min-width: 0;

    strong,
    p {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
    }

    p {
        margin: 1px 0 0;
        color: rgba(255, 255, 255, 0.58);
        font-size: 12px;
        line-height: 18px;
    }
}

.prompt-assistant-card__close,
.prompt-assistant-card__action {
    position: absolute;
    display: grid;
    place-items: center;
    border: 0;
    cursor: pointer;
}

.prompt-assistant-card__close {
    top: 6px;
    right: 8px;
    width: 18px;
    height: 18px;
    padding: 0;
    border-radius: 50%;
    background: transparent;
    color: rgba(255, 255, 255, 0.42);

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.9);
    }

    svg {
        width: 12px;
        height: 12px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
        stroke-linecap: round;
    }
}

.prompt-assistant-card__action {
    right: 10px;
    bottom: 12px;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.8);

    &:hover {
        background: rgba(255, 255, 255, 0.12);
        color: #fff;
    }

    svg {
        width: 16px;
        height: 16px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
}

.prompt-assistant-card--zeny {
    border-color: rgba(255, 255, 255, 0.1);
}

.prompt-assistant-card--vlo {
    border-color: rgba(255, 255, 255, 0.1);
}

.prompt-assistant-card--lydi {
    border-color: rgba(255, 255, 255, 0.1);
}

.prompt-assistant-card--history {
    --history-color: #69d8f6;
    position: relative;
    isolation: isolate;
    min-height: 104px;
    margin-top: 0;
    padding: 16px 232px 16px 28px;
    border: 1px solid transparent;
    border-radius: 14px;
    background:
        linear-gradient(110deg,
            #191b20 0%,
            #1b2028 48%,
            color-mix(in srgb, var(--history-color) 16%, #27223a) 100%) padding-box,
        linear-gradient(135deg,
            color-mix(in srgb, var(--history-color) 74%, #b9f6ff) 0%,
            rgba(24, 32, 46, 0.74) 16%,
            color-mix(in srgb, var(--history-color) 68%, #42b7d2) 29%,
            rgba(29, 28, 52, 0.72) 44%,
            rgba(112, 108, 228, 0.7) 58%,
            rgba(31, 25, 50, 0.72) 72%,
            color-mix(in srgb, var(--history-color) 62%, #a85bcd) 84%,
            color-mix(in srgb, var(--history-color) 74%, #b9f6ff) 100%) border-box;
    background-size: 100% 100%, 260% 100%;
    box-shadow: 0 0 18px color-mix(in srgb, var(--history-color) 16%, transparent);
    animation: history-assistant-border-flow 12s ease-in-out infinite;

    .prompt-assistant-card__avatar {
        position: relative;
        z-index: 2;
        width: 64px;
        height: 64px;
        flex-basis: 64px;
        padding: 0;
        border-color: rgba(255, 255, 255, 0.2);
        background: rgba(255, 255, 255, 0.075);
        object-position: center;
        transform: none;
    }

    .prompt-assistant-card__content {
        position: relative;
        z-index: 2;
        display: grid;
        min-height: 64px;
        align-content: center;

        .prompt-assistant-card__history-title {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;

            span {
                max-width: 150px;
                overflow: hidden;
                padding: 2px 9px;
                border: 1px solid color-mix(in srgb, var(--history-color) 24%, transparent);
                border-radius: 999px;
                background: color-mix(in srgb, var(--history-color) 12%, transparent);
                color: rgba(255, 255, 255, 0.74);
                font-size: 12px;
                font-weight: 500;
                line-height: 18px;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }

        strong {
            font-size: 20px;
            line-height: 26px;
        }

        p {
            margin-top: 12px;
            font-size: 14px;
            line-height: 20px;
        }
    }

    .prompt-assistant-card__action {
        top: 50%;
        right: 16px;
        z-index: 3;
        bottom: auto;
        width: 34px;
        height: 34px;
        border-color: rgba(255, 255, 255, 0.08);
        background: rgba(255, 255, 255, 0.055);
        color: #aeb8c5;
        transform: translateY(-50%);
    }

    &.prompt-assistant-card--zeny {
        --history-color: #ff83b7;
    }

    &.prompt-assistant-card--vlo {
        --history-color: #4dc9e7;
    }

    &.prompt-assistant-card--lydi {
        --history-color: #76d7ae;
    }
}

.prompt-assistant-card__history-symbols {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 44px;
    bottom: 0;
    width: 310px;
    pointer-events: none;
}

.prompt-assistant-card__history-symbol {
    position: absolute;
    display: block;
    color: var(--history-color);
    fill: none;
    opacity: 0.1;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.25;

    &.symbol-1 {
        top: 50%;
        right: 30px;
        width: 78px;
        height: 78px;
        opacity: 0.13;
        transform: translateY(-50%) rotate(10deg);
    }

    &.symbol-2 {
        right: 156px;
        bottom: 12px;
        width: 30px;
        height: 30px;
        opacity: 0.08;
        transform: rotate(7deg);
    }

    &.symbol-3 {
        top: 18px;
        right: 106px;
        width: 25px;
        height: 25px;
        opacity: 0.07;
        transform: rotate(-7deg);
    }
}

@keyframes history-assistant-border-flow {
    0% {
        background-position: 0 0, 0% 50%;
    }

    50% {
        background-position: 0 0, 128% 50%;
    }

    100% {
        background-position: 0 0, 260% 50%;
    }
}

@media (prefers-reduced-motion: reduce) {
    .prompt-assistant-card--history {
        animation: none;
    }
}

@media (max-width: 760px) {
    .prompt-assistant-card--history {
        min-height: 96px;
        gap: 12px;
        padding: 14px 126px 14px 14px;

        .prompt-assistant-card__content {
            min-height: 64px;

            strong {
                font-size: 15px;
            }

            p {
                margin-top: 9px;
                font-size: 12px;
            }
        }
    }

    .prompt-assistant-card__history-symbols {
        right: 40px;
        width: 158px;
    }

    .prompt-assistant-card__history-symbol {
        &.symbol-1 {
            right: 5px;
            width: 58px;
            height: 58px;
        }

        &.symbol-2 {
            right: 68px;
            width: 22px;
            height: 22px;
        }

        &.symbol-3 {
            right: 42px;
            width: 19px;
            height: 19px;
        }
    }
}
</style>
